/**
 * PageMeta — right-side metadata column for a notebook page.
 *
 * Phase 2 surfaces:
 *   - Version (increments on each save).
 *   - Created at / updated at.
 *   - Owner.
 *   - "Referenced by N tasks" with a click-through to the drawer for each
 *     reverse task. Real label chips and comment threads come in Phase 5.
 *
 * Hidden on screens narrower than `lg` — on mobile the meta is collapsed
 * into inline `details` blocks at the bottom of the page (Phase 7).
 *
 * Desktop-only collapse: on `lg+` the user can collapse the column to a
 * thin rail (~48px) for a fullscreen writing experience. State is
 * persisted as `friday:notebook-meta-collapsed` and survives reloads.
 * On `md` and below the column is already `hidden`, so the collapse
 * button itself is also `hidden lg:inline-flex` to avoid orphans.
 */

import { useEffect, useState } from 'react'
import { usePageTasks } from '@/features/notebook/pages'
import { useProfiles, pickDisplay } from '@/features/profiles/profiles'
import type { Page } from '@friday/shared/realtime'
import { cn } from '@/lib/utils'

const PAGE_META_COLLAPSED_KEY = 'friday:notebook-meta-collapsed'

export interface PageMetaProps {
  wsId: string
  page: Page
}

export function PageMeta({ wsId, page }: PageMetaProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    try {
      return window.localStorage.getItem(PAGE_META_COLLAPSED_KEY) === '1'
    } catch {
      return false
    }
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(PAGE_META_COLLAPSED_KEY, collapsed ? '1' : '0')
    } catch {
      /* private mode / quota — ignore, in-memory state still works */
    }
  }, [collapsed])

  const tasksQ = usePageTasks(page.id)
  const ownerProfiles = useProfiles([page.ownerUserId])
  const owner = pickDisplay(page.ownerUserId, ownerProfiles.profiles[page.ownerUserId])

  return (
    <aside
      className={cn(
        'relative hidden w-72 shrink-0 border-l border-surface-200 px-4 py-4 text-sm text-app lg:block dark:border-surface-800',
        // On `lg+` the collapse shrinks the column to an icon rail. Below
        // `lg` the column is `hidden` anyway, so this rule is scoped to
        // `lg:` to keep the surface clean on `md`.
        collapsed && 'lg:w-12 lg:px-1',
      )}
    >
      <div
        className={cn(
          'mb-3 flex items-center',
          collapsed ? 'lg:justify-center' : 'justify-between',
        )}
      >
        {!collapsed && (
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-surface-500">
            Page details
          </h3>
        )}
        {!collapsed && (
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="hidden rounded p-0.5 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800 lg:inline-flex"
            aria-label="Collapse page details"
            aria-pressed="false"
            title="Collapse page details"
          >
            «
          </button>
        )}
        {collapsed && (
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            // Anchored to the right edge of the collapsed column (4px in)
            // instead of straddling it. The previous `-right-3` put 12px
            // of the chip outside the viewport on the rightmost column,
            // so half the button was clipped. The Pages sidebar uses the
            // same chip but with `md:z-50` on the column so the
            // negative offset stays visible there.
            className="absolute right-1 top-3 z-10 hidden h-6 w-6 items-center justify-center rounded-full border border-surface-200 bg-white text-surface-600 shadow-sm hover:bg-surface-50 dark:border-surface-700 dark:bg-surface-800 dark:text-surface-200 dark:hover:bg-surface-700 lg:inline-flex"
            aria-label="Expand page details"
            aria-pressed="true"
            title="Expand page details"
          >
            »
          </button>
        )}
      </div>
      {!collapsed && (
        <>
          <dl className="space-y-3">
            <Row label="Version">
              <span className="rounded bg-surface-200 px-1.5 py-0.5 font-mono text-[11px] dark:bg-surface-800">v{page.version}</span>
            </Row>
            <Row label="Slug">
              <code className="rounded bg-surface-200 px-1.5 py-0.5 font-mono text-[11px] dark:bg-surface-800">{page.slug}</code>
            </Row>
            <Row label="Owner">{owner.displayName}</Row>
            <Row label="Created">{new Date(page.createdAt).toLocaleString()}</Row>
            <Row label="Updated">{new Date(page.updatedAt).toLocaleString()}</Row>
            {page.archivedAt && (
              <Row label="Archived">
                <span className="text-amber-600 dark:text-amber-300">{new Date(page.archivedAt).toLocaleString()}</span>
              </Row>
            )}
          </dl>
          <h3 className="mt-6 mb-2 text-[10px] font-semibold uppercase tracking-wider text-surface-500">Referenced by tasks</h3>
          {tasksQ.isLoading && <div className="text-xs text-surface-500">Loading…</div>}
          {tasksQ.data && tasksQ.data.length === 0 && <div className="text-xs text-surface-500">No tasks reference this page.</div>}
          {tasksQ.data && tasksQ.data.length > 0 && (
            <ul className="space-y-1 text-xs">
              {tasksQ.data.map((t) => (
                <li key={t.id} className="rounded border border-surface-200 px-2 py-1 dark:border-surface-700">
                  <span className="font-mono text-[10px] text-surface-500">{t.key}</span> {t.title}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </aside>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-surface-500">{label}</dt>
      <dd className={cn('mt-0.5 text-app')}>{children}</dd>
    </div>
  )
}
